import { useAuth0 as useAuth0Base } from '@auth0/auth0-react';
import { useCallback, useMemo } from 'react';

import {
  useGetClientUserMissingConsentsQuery as useGetClientUserMissingConsentsQueryBase,
  useGetClientUserWithParentGuardianInformation as useGetClientUserWithParentGuardianInformationBase,
  useGetCurrentClientAccountQuery as useGetCurrentClientAccountQueryBase,
} from '@grow-therapy-team/seedling-components';

import { logger as loggerBase } from '../../../datadog';
import { useAutoRetryOnError as useAutoRetryOnErrorBase } from '../../../useAutoRetryOnError';
import { useGetClientRequiresAuthedIntroRoom as useGetClientRequiresAuthedIntroRoomBase } from '../auth/useClientRequiresAuthedIntroRoom';

type Props = {
  flowId: string;
  shouldRetryFailedQueries?: boolean;
};

export type Dependencies = {
  logger: typeof loggerBase;
  useAuth0: typeof useAuth0Base;
  useAutoRetryOnError: typeof useAutoRetryOnErrorBase;
  useGetCurrentClientAccountQuery: typeof useGetCurrentClientAccountQueryBase;
  useGetClientRequiresAuthedIntroRoom: typeof useGetClientRequiresAuthedIntroRoomBase;
  useGetClientUserMissingConsentsQuery: typeof useGetClientUserMissingConsentsQueryBase;
  useGetClientUserWithParentGuardianInformation: typeof useGetClientUserWithParentGuardianInformationBase;
};

export const DEFAULT_DEPENDENCIES: Dependencies = {
  logger: loggerBase,
  useAuth0: useAuth0Base,
  useAutoRetryOnError: useAutoRetryOnErrorBase,
  useGetCurrentClientAccountQuery: useGetCurrentClientAccountQueryBase,
  useGetClientRequiresAuthedIntroRoom: useGetClientRequiresAuthedIntroRoomBase,
  useGetClientUserMissingConsentsQuery: useGetClientUserMissingConsentsQueryBase,
  useGetClientUserWithParentGuardianInformation: useGetClientUserWithParentGuardianInformationBase,
};

/**
 * Utility hook for fetching the intro room data, checking if any data is
 * missing, and determining if the client has outstanding unsigned required
 * legal consents.
 *
 * You'll note that we're much more heavy-handed and defensive when it comes to
 * ensuring we have this data than we typically are; this is because any failed
 * request or missing data here will result in the client being unable to sign
 * their missing consents, which will block them from entering the waiting room
 * and accessing their session. Instead, we aggressively retry failed requests,
 * assert that data is present in the responses, and log any issues extensively.
 *
 * If we are unable to get all of the data we need, we will allow the client to
 * enter the waiting room as not to block care.
 *
 * @param props - Primary props for the hook:
 * - flowId: The current unique ID of the client's trial of the intro room flow.
 *   This is used to track the client's progress through the flow in Segment.
 * - shouldRetryFailedQueries: Whether to retry failed queries.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns An object containing the loading state, whether any data is missing,
 * whether there are missing consents that the client needs to sign first, and
 * the actual data from each query.
 */
export function useIntroRoomData(
  { flowId, shouldRetryFailedQueries = false }: Props,
  {
    logger,
    useAuth0,
    useAutoRetryOnError,
    useGetCurrentClientAccountQuery,
    useGetClientRequiresAuthedIntroRoom,
    useGetClientUserMissingConsentsQuery,
    useGetClientUserWithParentGuardianInformation,
  }: Dependencies = DEFAULT_DEPENDENCIES,
) {
  const { isAuthenticated } = useAuth0();

  // NOTE: This data is pre-loaded with a retry mechanism in
  // WithVisitorRoutePreRender and should already be available in the cache, but
  // we'll check if it's loading/missing just in case.
  //
  // NOTE: This query indicates whether or not a client needs to sign any
  // missing required legal consents before they can be allowed to see their
  // provider; the check is referred to as "requiring auth" because
  // historically, signing consents required that users were first
  // authenticated, and telehealth does not require users to be authenticated to
  // join sessions under normal circumstances. However, today, you can treat
  // "requireAuth" as the same as "has missing consents".
  const { data: requiresAuthedIntroRoomData, loading: isLoadingRequiresAuthedIntroRoom } =
    useGetClientRequiresAuthedIntroRoom();

  const clientUserShortId = requiresAuthedIntroRoomData?.clientRequiresAuthedIntroRoom?.clientUserShortId ?? undefined;

  const {
    data: missingConsentsData,
    error: missingConsentsError,
    loading: isLoadingMissingConsents,
    refetch: refetchMissingConsents,
  } = useGetClientUserMissingConsentsQuery(
    clientUserShortId,
    // This query requires authentication and should only be executed if the
    // client is logged in. This data will come from the PII verification step
    // instead for unauthenticated clients.
    isAuthenticated,
    // Include missing Evernorth consent types in the query
    true,
  );

  const {
    data: clientUserWithParentGuardianData,
    error: clientUserWithParentGuardianError,
    loading: isLoadingClientUserParentGuardian,
    refetch: refetchClientUserWithParentGuardian,
  } = useGetClientUserWithParentGuardianInformation(
    clientUserShortId,
    // This query requires authentication and should only be executed if the
    // client is logged in.
    isAuthenticated,
  );

  const {
    data: currentClientAccountData,
    error: currentClientAccountError,
    loading: isLoadingCurrentClientAccount,
    refetch: refetchCurrentClientAccount,
  } = useGetCurrentClientAccountQuery(
    // This query requires authentication and should only be executed if the
    // client is logged in.
    isAuthenticated,
  );

  const onRetryFailure = useCallback(
    (queryName: string) => (details: { retryCount: number; isFinalAttempt: boolean; error: Error }) => {
      logger.warn(`Failed to fetch ${queryName} with retry`, { clientUserShortId, flowId, ...details }, details.error);
    },
    [clientUserShortId, flowId, logger],
  );

  const { onMissingConsentsFailure, onClientUserWithParentGuardianFailure, onCurrentClientAccountFailure } = useMemo(
    () => ({
      onMissingConsentsFailure: onRetryFailure('clientUserMissingConsents'),
      onClientUserWithParentGuardianFailure: onRetryFailure('clientUserWithParentGuardian'),
      onCurrentClientAccountFailure: onRetryFailure('currentClientAccount'),
    }),
    [onRetryFailure],
  );

  // Auto-retry authenticated queries on error
  const { isRetrying: isRetryingMissingConsents } = useAutoRetryOnError(
    shouldRetryFailedQueries ? missingConsentsError : undefined,
    refetchMissingConsents,
    onMissingConsentsFailure,
  );
  const { isRetrying: isRetryingClientUserWithParentGuardian } = useAutoRetryOnError(
    shouldRetryFailedQueries ? clientUserWithParentGuardianError : undefined,
    refetchClientUserWithParentGuardian,
    onClientUserWithParentGuardianFailure,
  );
  const { isRetrying: isRetryingCurrentClientAccount } = useAutoRetryOnError(
    shouldRetryFailedQueries ? currentClientAccountError : undefined,
    refetchCurrentClientAccount,
    onCurrentClientAccountFailure,
  );

  const isLoading =
    isLoadingRequiresAuthedIntroRoom ||
    isLoadingMissingConsents ||
    isLoadingClientUserParentGuardian ||
    isLoadingCurrentClientAccount ||
    isRetryingMissingConsents ||
    isRetryingClientUserWithParentGuardian ||
    isRetryingCurrentClientAccount;

  // Check if any of the data we expect to be present and require in order to
  // sign consents is missing, once all the network requests have completed.
  const isMissingData = useMemo(() => {
    if (isLoading) return false;
    // Both authenticated and unauthenticated clients should have this data.
    if (!requiresAuthedIntroRoomData || !requiresAuthedIntroRoomData.clientRequiresAuthedIntroRoom) return true;

    if (!isAuthenticated) return false;

    // Authenticated clients should additionally have this data.
    return (
      !missingConsentsData ||
      !missingConsentsData.clientUserMissingConsents ||
      !clientUserWithParentGuardianData ||
      !clientUserWithParentGuardianData.clientUser
      // Client account data is not blocking
    );
  }, [isAuthenticated, isLoading, missingConsentsData, clientUserWithParentGuardianData, requiresAuthedIntroRoomData]);

  // If any of the data is missing, we will allow the client to enter the
  // waiting room as not to block care, but log a warning. This likely indicates
  // a network blip, a monolith outage, a resolver bug, or requests being
  // dropped by the monolith (e.g. due to top-of-hour traffic spikes).
  const requiresWaitingRoomBlock = useMemo(() => {
    if (isMissingData) return false;
    return requiresAuthedIntroRoomData?.clientRequiresAuthedIntroRoom?.requireAuth;
  }, [isMissingData, requiresAuthedIntroRoomData]);

  const isEvernorthPatient = requiresAuthedIntroRoomData?.publicPatient?.isEvernorthPatient ?? false;

  return {
    isLoading,
    isMissingData,
    requiresWaitingRoomBlock,
    isEvernorthPatient,
    data: {
      requiresAuthedIntroRoomData,
      missingConsentsData,
      clientUserWithParentGuardianData,
      currentClientAccountData,
    },
  };
}
